import React from "react";
import { Box, Stack } from "@mui/joy";
import type { ZekayConfig } from "../../api/config/Config.typed";
import useAppSettings from "../../contexts/app/appSettings";
import { useConfig } from "../../contexts/api";
import { useAlert } from "../../contexts/app/notificationToast";
import { Permission, usePermissions } from "../../contexts/app/permissions";
import { useConfirmationModal } from "../../contexts/app/confirmation";
import CentreExternalDataSection from "./display/CentreExternalDataSection";
import CentreEntrepriseSection from "./display/CentreEntrepriseSection";

export default function CentreInfoPage() {
  const { config, reloadConfig, saveConfig, setConfigLocal } = useAppSettings();
  const { uploadConfigAsset, deleteConfigAsset } = useConfig();
  const { showAlert } = useAlert();
  const { hasPermission } = usePermissions();
  const { showConfirmation } = useConfirmationModal();
  const canEdit = hasPermission(Permission.CENTRE_INFO_EDIT);
  const [isUploadingLogo, setIsUploadingLogo] = React.useState<false | "logo" | "qr">(false);

  React.useEffect(() => {
    if (!config) void reloadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUploadLogo = async (target: "logo" | "qr", file?: File | null) => {
    if (!file || !config) return;
    if (target === "logo") {
      const isWebp = file.type === "image/webp" || file.name.toLowerCase().endsWith(".webp");
      if (!isWebp) {
        showAlert("Format accepté pour le logo : WebP uniquement.", "warning");
        return;
      }
    } else {
      const isAllowed =
        file.type.startsWith("image/") || file.name.toLowerCase().match(/\.(png|svg|jpe?g|webp)$/);
      if (!isAllowed) {
        showAlert("Formats acceptés pour le QR : PNG, SVG, JPG, WebP.", "warning");
        return;
      }
    }
    try {
      setIsUploadingLogo(target);
      const suggestedName = target === "logo" ? "logo" : "logo_qr";
      const defaultExt =
        target === "logo" ? ".webp" : file.type === "image/svg+xml" ? ".svg" : ".png";
      const res = await uploadConfigAsset(
        file,
        suggestedName + (file.name.includes(".") ? "" : defaultExt),
        target === "logo" ? "logo" : "logo_qr",
      );
      if (!res) return;
      const next = {
        ...config,
        ...(target === "logo" ? { LogoUrl: res.URL } : { LogoQrCodeUrl: res.URL }),
      } as ZekayConfig;
      setConfigLocal(() => next);
      try {
        await saveConfig(next);
        showAlert("Logo sauvegardé", "success");
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Échec de l'enregistrement";
        showAlert(msg, "danger");
      }
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleRemoveLogoUrl = async (target: "logo" | "qr") => {
    const ok = await showConfirmation(
      target === "logo" ? "Supprimer le logo" : "Supprimer le logo QR",
      "L’URL et les fichiers hébergés seront supprimés côté serveur.",
    );
    if (!ok) return;

    const field = target === "logo" ? "logo" : "logo_qr";
    const removed = await deleteConfigAsset(field);
    if (removed) void reloadConfig();
  };

  if (!config) return null;

  return (
    <Box sx={{ width: "100%", padding: 2, overflowY: "auto", height: "100%", maxHeight: "100%" }}>
      <Stack spacing={2} sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
        <CentreEntrepriseSection
          config={config}
          IsUploadingLogo={isUploadingLogo}
          OnUploadLogo={(file) => handleUploadLogo("logo", file)}
          OnUploadQr={(file) => handleUploadLogo("qr", file)}
          OnRemoveLogo={() => void handleRemoveLogoUrl("logo")}
          OnRemoveQr={() => void handleRemoveLogoUrl("qr")}
          canEditBackend={canEdit}
        />

        <CentreExternalDataSection config={config} canEditBackend={canEdit} />
      </Stack>
    </Box>
  );
}
